import { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Printer, Lock } from "lucide-react";

interface DiaEspelho {
  data: string;
  minutosTrabalhados: number;
  minutosPrevistos: number;
  minutosExtras50: number;
  minutosExtras100: number;
  minutosNoturnos: number;
  minutosSaldo: number;
  saldoBancoHorasAcumulado: number;
  inconsistente: boolean;
  tipoDiaTexto: string;
}

interface Espelho {
  funcionarioNome: string;
  dtInicio: string;
  dtFim: string;
  saldoFinalMin: number;
  totalExtras50Min: number;
  totalExtras100Min: number;
  totalAdicionalNoturnoPonderadoMin: number;
  reflexoDsrMin: number;
  dias: DiaEspelho[];
}

/** minutos → "±HH:MM" */
function hm(min: number): string {
  const s = min < 0 ? "-" : "";
  const a = Math.abs(min);
  return `${s}${String(Math.floor(a / 60)).padStart(2, "0")}:${String(a % 60).padStart(2, "0")}`;
}

const hoje = new Date();
const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().slice(0, 10);
const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().slice(0, 10);

export default function EspelhoPontoPage() {
  const [funcionarioId, setFuncionarioId] = useState("");
  const [jornadaId, setJornadaId] = useState("");
  const [inicio, setInicio] = useState(primeiroDia);
  const [fim, setFim] = useState(ultimoDia);
  const [espelho, setEspelho] = useState<Espelho | null>(null);
  const [carregando, setCarregando] = useState(false);

  const gerar = async () => {
    if (!funcionarioId) {
      toast.warning("Informe o funcionário.");
      return;
    }
    setCarregando(true);
    try {
      const r = await apiClient.get<Espelho>("/api/rh/ponto/espelho", {
        params: {
          funcionarioId: Number(funcionarioId),
          jornadaId: Number(jornadaId) || 0,
          inicio,
          fim,
          saldoInicialMin: 0,
        },
      });
      setEspelho(r.data);
    } catch (e: any) {
      toast.error(e.message ?? "Falha ao gerar o espelho.");
    } finally {
      setCarregando(false);
    }
  };

  const fecharBanco = async () => {
    if (!funcionarioId) return;
    try {
      const r = await apiClient.post("/api/rh/ponto/banco-horas/fechar", null, {
        params: { funcionarioId: Number(funcionarioId), inicio, fim },
      });
      toast.success(r.data?.mensagem ?? "Banco de horas fechado.");
    } catch (e: any) {
      toast.error(e.message ?? "Falha ao fechar o banco de horas.");
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Espelho de Ponto</h1>
          <p className="text-sm text-muted-foreground">Apuração do período (REP-P) — batidas, extras e banco de horas.</p>
        </div>
        {espelho && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={fecharBanco}>
              <Lock className="h-4 w-4 mr-2" />Fechar banco de horas
            </Button>
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />Imprimir / PDF
            </Button>
          </div>
        )}
      </div>

      <Card className="print:hidden">
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 pt-6">
          <div className="space-y-1">
            <Label>Funcionário (Id)</Label>
            <Input type="number" value={funcionarioId} onChange={(e) => setFuncionarioId(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Jornada (Id)</Label>
            <Input type="number" value={jornadaId} onChange={(e) => setJornadaId(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Início</Label>
            <Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Fim</Label>
            <Input type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button onClick={gerar} disabled={carregando} className="w-full">
              <Search className="h-4 w-4 mr-2" />{carregando ? "Gerando…" : "Gerar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {espelho && (
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap gap-x-6 gap-y-1 text-base font-normal">
              <span><b>Funcionário:</b> {espelho.funcionarioNome || funcionarioId}</span>
              <span><b>Período:</b> {espelho.dtInicio?.slice(0, 10)} a {espelho.dtFim?.slice(0, 10)}</span>
              <span><b>Saldo banco:</b> {hm(espelho.saldoFinalMin)}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Trabalhado</TableHead>
                    <TableHead className="text-right">Previsto</TableHead>
                    <TableHead className="text-right">Extra 50%</TableHead>
                    <TableHead className="text-right">Extra 100%</TableHead>
                    <TableHead className="text-right">Noturno</TableHead>
                    <TableHead className="text-right">Saldo dia</TableHead>
                    <TableHead className="text-right">Acumulado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {espelho.dias.map((d) => (
                    <TableRow key={d.data} className={d.inconsistente ? "bg-destructive/5" : undefined}>
                      <TableCell className="whitespace-nowrap">
                        {d.data.slice(0, 10)}
                        {d.inconsistente && <Badge variant="destructive" className="ml-2">!</Badge>}
                      </TableCell>
                      <TableCell>{d.tipoDiaTexto}</TableCell>
                      <TableCell className="text-right">{hm(d.minutosTrabalhados)}</TableCell>
                      <TableCell className="text-right">{hm(d.minutosPrevistos)}</TableCell>
                      <TableCell className="text-right">{hm(d.minutosExtras50)}</TableCell>
                      <TableCell className="text-right">{hm(d.minutosExtras100)}</TableCell>
                      <TableCell className="text-right">{hm(d.minutosNoturnos)}</TableCell>
                      <TableCell className="text-right">{hm(d.minutosSaldo)}</TableCell>
                      <TableCell className="text-right">{hm(d.saldoBancoHorasAcumulado)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-wrap gap-x-8 gap-y-1 mt-4 text-sm">
              <span><b>Total extra 50%:</b> {hm(espelho.totalExtras50Min)}</span>
              <span><b>Total extra 100%:</b> {hm(espelho.totalExtras100Min)}</span>
              <span><b>Adic. noturno:</b> {hm(espelho.totalAdicionalNoturnoPonderadoMin)}</span>
              <span><b>Reflexo DSR:</b> {hm(espelho.reflexoDsrMin)}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
